import { useEffect, useMemo, useRef, useState } from 'react';
import { truncateGraphemes } from '@tc/domain';
import { getState, openConversation, useStore } from '../store.ts';
import { formatTime, useT, useUiLocale } from '../i18n.ts';
import { Seal, conversationLocales, conversationTitle, readableBody, systemText } from './common.tsx';
import type { Panel } from './Shell.tsx';
import { SearchResults } from './Search.tsx';
import { Brand } from './Brand.tsx';
import { api } from '../api.ts';
import { isNative } from '../native.ts';
import { desktop } from '../desktop.ts';

export function Sidebar({ onPanel }: { onPanel: (p: Panel) => void }) {
  // "Download the app" next to your name, in the browser only (not inside the apps), when there is something to get.
  const [hasApps, setHasApps] = useState(false);
  useEffect(() => {
    if (isNative || desktop) return;
    api<{ items: unknown[] }>('GET', '/api/downloads').then((r) => setHasApps(r.items.length > 0)).catch(() => {});
  }, []);
  const t = useT();
  const ui = useUiLocale();
  const me = useStore((s) => s.me)!;
  const convs = useStore((s) => s.conversations);
  const activeId = useStore((s) => s.activeId);
  const [query, setQuery] = useState('');

  const list = useMemo(
    () => Object.values(convs).sort((a, b) => (b.lastMessage?.createdAt ?? b.updatedAt).localeCompare(a.lastMessage?.createdAt ?? a.updatedAt)),
    [convs],
  );

  const searchRef = useRef<HTMLInputElement>(null);

  // ⌘K / Ctrl+K jumps to message search from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (getState().activeId && window.matchMedia('(max-width: 760px)').matches) void openConversation(null);
        requestAnimationFrame(() => searchRef.current?.focus());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <aside className="sidebar" aria-label={t('conversations')}>
      <header className="sidebar-head">
        <Brand small />
        <div className="sidebar-actions">
          {me.isAdmin && (
            <button className="icon-btn head-btn" onClick={() => onPanel('admin')} aria-label={t('admin')} title={t('admin')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
                <path d="M9.5 12l1.8 1.8 3.5-3.6" />
              </svg>
            </button>
          )}
          <button className="icon-btn head-btn primary" onClick={() => onPanel('new')} aria-label={t('newChat')} title={t('newChat')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
      </header>
      <div className="search">
        <input
          ref={searchRef}
          type="search"
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && setQuery('')}
        />
      </div>
      {query.trim() ? (
        <SearchResults query={query} showConversation />
      ) : (
        <ul className="conv-list" role="list">
          {list.length === 0 && <li className="empty">{t('noConversations')}</li>}
          {list.map((c) => {
            const lm = c.lastMessage;
            const preview = !lm ? '' : lm.kind === 'system' ? systemText(lm, ui) : lm.deletedAt ? t('deleted') : truncateGraphemes(readableBody(lm), 90);
            const locales = conversationLocales(c, me.id);
            return (
              <li key={c.id}>
                <button className={`conv ${c.id === activeId ? 'active' : ''} ${c.unreadCount ? 'unread' : ''}`} onClick={() => void openConversation(c.id)} aria-current={c.id === activeId}>
                  <span className={`seal-stack n${Math.min(locales.length, 3)}`} aria-hidden="true">
                    {locales.slice(0, 3).map((l) => <Seal key={l} locale={l} small />)}
                  </span>
                  <div className="conv-main">
                    <div className="conv-top">
                      <span className="conv-title">{conversationTitle(c, me.id)}</span>
                      {lm && <time className="conv-time">{formatTime(lm.createdAt, ui)}</time>}
                    </div>
                    <div className="conv-bottom">
                      <span className="conv-preview">{preview}</span>
                      {c.unreadMentions > 0 && <span className="badge mention-badge" aria-label={t('mentionedYou')} title={t('mentionedYou')}>@</span>}
                      {c.unreadCount > 0 && <span className="badge" aria-label={`${c.unreadCount}`}>{c.unreadCount}</span>}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <footer className="sidebar-foot">
        <button className="me-btn" onClick={() => onPanel('settings')} title={t('settings')}>
          <span className="me-avatar" aria-hidden="true">{initials(me.displayName)}</span>
          <span className="me-text">
            <span className="me-name">{me.displayName}</span>
            <span className="me-sub">
              <Seal locale={me.readingLocale} small />
              {me.organizationName ?? t('settings')}
            </span>
          </span>
        </button>
        {hasApps && (
          <button className="get-app-btn" onClick={() => onPanel('apps')} title={t('downloadApp')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19h14" />
            </svg>
            <span>{t('downloadApp')}</span>
          </button>
        )}
      </footer>
    </aside>
  );
}

/** "Johan Jakobsson" → "JJ", "李伟 Li Wei" → "李", "anna" → "A". */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const first = [...words[0]!][0]!;
  if (/\p{Script=Han}/u.test(first)) return first;
  const last = words.length > 1 ? [...words[words.length - 1]!][0]! : '';
  return (first + last).toUpperCase();
}
